import { useMemo } from 'react';
import { useCurrentAgent } from '@/features/chats/stores';

/**
 * A hook that filters a list of models based on the current agent's enabled models.
 *
 * The hook recieves a list of model objects, then returns a list of filtered down model objects
 * based on what is whitelisted for the current agent.
 *
 * This will help avoid showing non-whitelisted models on the model selector for agents
 *
 * @param allModels - The list of all available models
 * @param keySelector - Optional function to extract the key to compare against enabled model's key. Defaults to using 'value' property.
 */
export const useFilteredModels = <T extends { value: string }>(
  allModels: ReadonlyArray<T>,
  keySelector?: (model: T) => string,
): T[] => {
  const agent = useCurrentAgent();

  return useMemo(() => {
    // If no agent is selected or no enabled models are specified, return empty array
    // This triggers the "No models available" UI in ModelsDropdown component
    if (!agent?.enabledModels || agent.enabledModels.length === 0) {
      return [];
    }

    // Use the keySelector if provided, otherwise default to model.value
    const getKey = keySelector || ((model: T) => model.value);

    // Filter models based on the agent's enabled models
    return allModels.filter((model) => agent.enabledModels!.includes(getKey(model)));
  }, [agent, allModels, keySelector]);
};
